import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import type {
  CompanyServiceCoinLimitCoinResponse,
  CompanyServiceCoinLimitGroupResponse,
  CompanyServiceCoinLimitItem,
  CompanyServiceCoinLimitsUpsertPayload,
} from './companies/company-service-coin-limits.service';

export type BranchServiceCoinLimitItem = CompanyServiceCoinLimitItem;
export type BranchServiceCoinLimitCoinResponse =
  CompanyServiceCoinLimitCoinResponse;
export type BranchServiceCoinLimitGroupResponse =
  CompanyServiceCoinLimitGroupResponse;
export type BranchServiceCoinLimitsUpsertPayload =
  CompanyServiceCoinLimitsUpsertPayload;

export interface BranchServiceCoinLimits {
  branchId: string;
  serviceId: string;
  groups: BranchServiceCoinLimitGroupResponse[];
}

/**
 * Monedas y límites por servicio de sucursal (drawer «Monedas y límites»).
 *
 * GET/PUT `/api/structure/branches/{branchId}/services/{serviceId}/coin-limits`
 */
@Injectable({ providedIn: 'root' })
export class BranchServiceCoinLimitsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private branchesUrl(branchId: string, serviceId: string): string {
    return `${this.apiConfig.apiUrl}/structure/branches/${branchId}/services/${serviceId}/coin-limits`;
  }

  get(
    branchId: string,
    serviceId: string,
  ): Observable<BranchServiceCoinLimits> {
    return this.httpClient
      .get<ApiResponse<BranchServiceCoinLimits>>(
        this.branchesUrl(branchId, serviceId),
      )
      .pipe(map((response) => this.normalize(response.data, branchId, serviceId)));
  }

  update(
    branchId: string,
    serviceId: string,
    payload: BranchServiceCoinLimitsUpsertPayload,
  ): Observable<BranchServiceCoinLimits> {
    return this.httpClient
      .put<ApiResponse<BranchServiceCoinLimits>>(
        this.branchesUrl(branchId, serviceId),
        { items: payload.items ?? [] },
      )
      .pipe(
        map((response) =>
          this.normalize(response.data, branchId, serviceId),
        ),
      );
  }

  private normalize(
    data: BranchServiceCoinLimits | undefined,
    branchId: string,
    serviceId: string,
  ): BranchServiceCoinLimits {
    if (data?.groups) {
      return {
        branchId: data.branchId || branchId,
        serviceId: data.serviceId || serviceId,
        groups: data.groups.map((group) => ({
          serviceCoinId: group.serviceCoinId,
          serviceCoinCode: group.serviceCoinCode,
          serviceCoinName: group.serviceCoinName,
          coins: Array.isArray(group.coins) ? group.coins : [],
        })),
      };
    }
    return {
      branchId,
      serviceId,
      groups: [],
    };
  }
}
